// Facility catalogs for the 3 facility cards.
// Each facility entry has:
//   kind    — stable machine key stored in DB
//   label   — human name
//   fields  — array of field definitions the admin fills once the facility is enabled
//
// Field definitions:
//   { key, label, type, placeholder?, options?, help? }
//   type ∈ text | number | textarea | select | multiselect | checkbox

// Common field snippets so we don't repeat ourselves.
const LOCATION = [
  { key: "building", label: "Building", type: "text", placeholder: "Main Block" },
  { key: "floor", label: "Floor", type: "text", placeholder: "Ground / 1st / …" },
];
const CAPACITY = [{ key: "capacity", label: "Capacity", type: "number", placeholder: "40" }];
const CONTACT_PERSON = [{ key: "in_charge", label: "In-charge", type: "text", placeholder: "Name of the person responsible" }];

export const ACADEMIC_FACILITIES = [
  {
    kind: "library",
    label: "Library",
    fields: [
      { key: "name", label: "Library Name", type: "text", placeholder: "Central Library" },
      ...CAPACITY,
      { key: "books", label: "Books", type: "number" },
      { key: "reading_hall", label: "Reading Hall", type: "checkbox" },
      { key: "rfid", label: "RFID enabled", type: "checkbox" },
      { key: "automation_software", label: "Automation Software", type: "text", placeholder: "Koha / Libsys / other" },
      { key: "librarian", label: "Librarian", type: "text" },
      { key: "barcode", label: "Barcode", type: "checkbox" },
      ...LOCATION,
    ],
  },
  { kind: "digital_library", label: "Digital Library", fields: [...CAPACITY, { key: "e_resources", label: "E-Resources", type: "text", placeholder: "JSTOR, ProQuest, …" }, ...LOCATION] },
  { kind: "physics_lab", label: "Physics Lab", fields: [...CAPACITY, ...CONTACT_PERSON, { key: "equipment", label: "Equipment", type: "textarea" }, ...LOCATION] },
  { kind: "chemistry_lab", label: "Chemistry Lab", fields: [...CAPACITY, ...CONTACT_PERSON, { key: "safety_eq", label: "Safety Equipment", type: "textarea" }, ...LOCATION] },
  { kind: "biology_lab", label: "Biology Lab", fields: [...CAPACITY, ...CONTACT_PERSON, { key: "specimens", label: "Specimens available", type: "checkbox" }, ...LOCATION] },
  { kind: "computer_lab", label: "Computer Lab", fields: [...CAPACITY, { key: "computers", label: "Computers", type: "number" }, { key: "internet", label: "Internet", type: "checkbox" }, ...LOCATION] },
  { kind: "language_lab", label: "Language Lab", fields: [...CAPACITY, { key: "languages", label: "Languages Offered", type: "text", placeholder: "English, Hindi, French, …" }, ...LOCATION] },
  { kind: "robotics_lab", label: "Robotics Lab", fields: [...CAPACITY, { key: "kits", label: "Robotics Kits", type: "number" }, ...LOCATION] },
  { kind: "innovation_lab", label: "Innovation Lab", fields: [...CAPACITY, { key: "3d_printer", label: "3D Printer", type: "checkbox" }, ...LOCATION] },
  { kind: "ai_lab", label: "AI Lab", fields: [...CAPACITY, { key: "gpu_workstations", label: "GPU Workstations", type: "number" }, ...LOCATION] },
  { kind: "research_centre", label: "Research Centre", fields: [{ key: "focus_area", label: "Focus Area", type: "text" }, ...CAPACITY, ...LOCATION] },
  { kind: "seminar_hall", label: "Seminar Hall", fields: [...CAPACITY, { key: "av_setup", label: "AV Setup", type: "checkbox" }, ...LOCATION] },
  { kind: "auditorium", label: "Auditorium", fields: [...CAPACITY, { key: "stage", label: "Stage", type: "checkbox" }, { key: "green_room", label: "Green Room", type: "checkbox" }, ...LOCATION] },
  { kind: "conference_hall", label: "Conference Hall", fields: [...CAPACITY, { key: "video_conf", label: "Video Conferencing", type: "checkbox" }, ...LOCATION] },
  { kind: "music_room", label: "Music Room", fields: [...CAPACITY, { key: "instruments", label: "Instruments", type: "textarea" }, ...LOCATION] },
  { kind: "dance_room", label: "Dance Room", fields: [...CAPACITY, { key: "flooring", label: "Flooring Type", type: "text", placeholder: "Sprung wood / vinyl" }, ...LOCATION] },
  { kind: "art_room", label: "Art Room", fields: [...CAPACITY, { key: "kilns", label: "Kilns", type: "number" }, ...LOCATION] },
  { kind: "makerspace", label: "Makerspace", fields: [...CAPACITY, { key: "tools", label: "Tools", type: "textarea" }, ...LOCATION] },
  { kind: "planetarium", label: "Planetarium", fields: [...CAPACITY, { key: "dome_diameter_m", label: "Dome Diameter (m)", type: "number" }, ...LOCATION] },
  { kind: "observatory", label: "Observatory", fields: [{ key: "telescope", label: "Primary Telescope", type: "text" }, ...LOCATION] },
];

const SPORTS_COMMON = [
  { key: "availability", label: "Availability", type: "select", options: ["available", "seasonal", "under_maintenance", "closed"] },
  { key: "dimensions", label: "Dimensions", type: "text", placeholder: "e.g. 100m × 60m" },
  ...CAPACITY,
  { key: "coach", label: "Coach", type: "text" },
  { key: "equipment", label: "Equipment", type: "textarea", placeholder: "Balls, nets, racquets…" },
  { key: "booking_allowed", label: "Booking Allowed", type: "checkbox" },
  { key: "maintenance_status", label: "Maintenance Status", type: "select", options: ["ok", "attention_required", "under_service"] },
];

export const SPORTS_FACILITIES = [
  { kind: "football", label: "Football Field" },
  { kind: "cricket", label: "Cricket Ground" },
  { kind: "basketball", label: "Basketball Court" },
  { kind: "volleyball", label: "Volleyball Court" },
  { kind: "badminton", label: "Badminton Court" },
  { kind: "table_tennis", label: "Table Tennis" },
  { kind: "swimming_pool", label: "Swimming Pool" },
  { kind: "indoor_stadium", label: "Indoor Stadium" },
  { kind: "athletics_track", label: "Athletics Track" },
  { kind: "gymnasium", label: "Gymnasium" },
  { kind: "yoga_hall", label: "Yoga Hall" },
  { kind: "skating", label: "Skating Rink" },
  { kind: "martial_arts", label: "Martial Arts" },
].map((f) => ({ ...f, fields: [...SPORTS_COMMON] }));

export const STUDENT_FACILITIES = [
  { kind: "transport", label: "Transport", fields: [{ key: "buses", label: "Buses", type: "number" }, { key: "routes", label: "Routes", type: "number" }, { key: "gps_tracking", label: "GPS Tracking", type: "checkbox" }, ...CONTACT_PERSON] },
  { kind: "hostel", label: "Hostel", fields: [{ key: "beds", label: "Beds", type: "number" }, { key: "boys_capacity", label: "Boys Capacity", type: "number" }, { key: "girls_capacity", label: "Girls Capacity", type: "number" }, { key: "warden", label: "Warden", type: "text" }, ...LOCATION] },
  { kind: "medical_room", label: "Medical Room", fields: [{ key: "beds", label: "Beds", type: "number" }, { key: "on_duty_hours", label: "On-duty Hours", type: "text" }, ...CONTACT_PERSON, ...LOCATION] },
  { kind: "clinic", label: "Clinic / Infirmary", fields: [{ key: "doctor", label: "Doctor", type: "text" }, { key: "hours", label: "Hours", type: "text" }, ...LOCATION] },
  { kind: "counselling", label: "Counselling", fields: [{ key: "counsellors", label: "Counsellors", type: "number" }, { key: "hours", label: "Hours", type: "text" }, ...LOCATION] },
  { kind: "cafeteria", label: "Cafeteria", fields: [...CAPACITY, { key: "menu_link", label: "Menu URL", type: "text" }, { key: "vendor", label: "Vendor", type: "text" }, ...LOCATION] },
  { kind: "mess", label: "Mess", fields: [...CAPACITY, { key: "meal_plans", label: "Meal Plans", type: "text", placeholder: "Veg / Non-veg / Jain" }, ...LOCATION] },
  { kind: "waiting_lounge", label: "Waiting Lounge", fields: [...CAPACITY, ...LOCATION] },
  { kind: "common_room", label: "Common Room", fields: [...CAPACITY, { key: "amenities", label: "Amenities", type: "textarea" }, ...LOCATION] },
  { kind: "book_store", label: "Book Store", fields: [{ key: "vendor", label: "Vendor", type: "text" }, { key: "hours", label: "Hours", type: "text" }, ...LOCATION] },
  { kind: "bank", label: "Bank", fields: [{ key: "bank_name", label: "Bank Name", type: "text" }, { key: "hours", label: "Hours", type: "text" }, ...LOCATION] },
  { kind: "atm", label: "ATM", fields: [{ key: "bank_name", label: "Bank", type: "text" }, ...LOCATION] },
  { kind: "parking", label: "Parking", fields: [{ key: "car_slots", label: "Car Slots", type: "number" }, { key: "bike_slots", label: "Bike Slots", type: "number" }, { key: "covered", label: "Covered", type: "checkbox" }] },
  { kind: "prayer_hall", label: "Prayer Hall", fields: [...CAPACITY, ...LOCATION] },
  { kind: "student_lounge", label: "Student Lounge", fields: [...CAPACITY, { key: "amenities", label: "Amenities", type: "textarea" }, ...LOCATION] },
  { kind: "innovation_centre", label: "Innovation Centre", fields: [...CAPACITY, { key: "focus_area", label: "Focus Area", type: "text" }, ...LOCATION] },
];

export const CATALOGS = {
  academic: ACADEMIC_FACILITIES,
  sports: SPORTS_FACILITIES,
  student: STUDENT_FACILITIES,
};

// ---------------------------------------------------------------------------
// Phase 3 catalogs — Smart Campus, Safety & Security, Utilities, Assets.
// Each catalog reuses the toggle-to-reveal panel by supplying its own
// per-kind field list. `resource` on FacilitiesPanel points to the correct
// backend collection.
// ---------------------------------------------------------------------------

const SMART_COMMON = [
  { key: "vendor", label: "Vendor", type: "text" },
  { key: "install_date", label: "Installed On", type: "text", placeholder: "YYYY-MM-DD" },
  { key: "coverage", label: "Coverage", type: "text", placeholder: "e.g. Entire campus / A block only" },
  { key: "warranty_until", label: "Warranty Until", type: "text", placeholder: "YYYY-MM-DD" },
  { key: "amc", label: "AMC in place", type: "checkbox" },
  { key: "amc_expiry", label: "AMC Expiry", type: "text", placeholder: "YYYY-MM-DD" },
];

export const SMART_CAMPUS_ITEMS = [
  { kind: "wifi", label: "WiFi Network" },
  { kind: "smart_boards", label: "Smart Boards" },
  { kind: "interactive_displays", label: "Interactive Displays" },
  { kind: "projectors", label: "Projectors" },
  { kind: "digital_classrooms", label: "Digital Classrooms" },
  { kind: "cctv", label: "CCTV" },
  { kind: "rfid", label: "RFID" },
  { kind: "biometric", label: "Biometric Access" },
  { kind: "face_recognition", label: "Face Recognition" },
  { kind: "digital_notice_boards", label: "Digital Notice Boards" },
  { kind: "iot_sensors", label: "IoT Sensors" },
  { kind: "gps_bus_tracking", label: "GPS Bus Tracking" },
  { kind: "parent_app", label: "Parent App" },
  { kind: "student_app", label: "Student App" },
  { kind: "faculty_app", label: "Faculty App" },
].map((f) => ({ ...f, fields: SMART_COMMON }));

const SAFETY_COMMON = [
  { key: "inspection_date", label: "Last Inspection", type: "text", placeholder: "YYYY-MM-DD" },
  { key: "compliance_status", label: "Compliance Status", type: "select", options: ["compliant", "action_required", "expired"] },
  { key: "expiry", label: "Certificate Expiry", type: "text", placeholder: "YYYY-MM-DD" },
  { key: "certificate_number", label: "Certificate #", type: "text" },
  { key: "remarks", label: "Remarks", type: "textarea" },
];

export const SAFETY_ITEMS = [
  { kind: "fire_alarm", label: "Fire Alarm System" },
  { kind: "fire_extinguishers", label: "Fire Extinguishers" },
  { kind: "emergency_exit", label: "Emergency Exit Signage" },
  { kind: "visitor_management", label: "Visitor Management" },
  { kind: "security_guards", label: "Security Guards" },
  { kind: "wheelchair_access", label: "Wheelchair Access" },
  { kind: "barrier_free", label: "Barrier-Free Design" },
  { kind: "ramps", label: "Ramps" },
  { kind: "earthquake_safe", label: "Earthquake-Safe Construction" },
  { kind: "strong_room", label: "Strong Room" },
  { kind: "disaster_recovery_plan", label: "Disaster Recovery Plan" },
  { kind: "emergency_contacts", label: "Emergency Contacts" },
  { kind: "security_office", label: "Security Office" },
  { kind: "incident_reporting", label: "Incident Reporting" },
].map((f) => ({ ...f, fields: SAFETY_COMMON }));

const UTILITY_COMMON = [
  { key: "vendor", label: "Vendor / Provider", type: "text" },
  { key: "capacity", label: "Capacity", type: "text", placeholder: "e.g. 500 KVA / 10 000 L" },
  { key: "maintenance_schedule", label: "Maintenance Schedule", type: "text", placeholder: "Monthly / Quarterly" },
  { key: "status", label: "Status", type: "select", options: ["operational", "backup_only", "under_service", "decommissioned"] },
  { key: "last_service", label: "Last Service", type: "text", placeholder: "YYYY-MM-DD" },
  { key: "notes", label: "Notes", type: "textarea" },
];

export const UTILITIES = [
  { kind: "electricity", label: "Electricity" },
  { kind: "generator", label: "Generator" },
  { kind: "solar", label: "Solar" },
  { kind: "water_supply", label: "Water Supply" },
  { kind: "water_purifier", label: "Water Purifier" },
  { kind: "rain_water_harvesting", label: "Rain-Water Harvesting" },
  { kind: "sewage_treatment", label: "Sewage Treatment" },
  { kind: "waste_management", label: "Waste Management" },
  { kind: "internet", label: "Internet" },
  { kind: "power_backup", label: "UPS / Power Backup" },
  { kind: "ev_charging", label: "EV Charging" },
  { kind: "garden", label: "Garden" },
  { kind: "landscape", label: "Landscape" },
].map((f) => ({ ...f, fields: UTILITY_COMMON }));

const ASSET_COMMON = [
  { key: "count", label: "Total Count", type: "number" },
  { key: "vendor", label: "Primary Vendor", type: "text" },
  { key: "purchase_date", label: "Purchase Date", type: "text", placeholder: "YYYY-MM-DD" },
  { key: "warranty_until", label: "Warranty Until", type: "text", placeholder: "YYYY-MM-DD" },
  { key: "amc", label: "Under AMC", type: "checkbox" },
  { key: "amc_expiry", label: "AMC Expiry", type: "text", placeholder: "YYYY-MM-DD" },
  { key: "assigned_department", label: "Assigned Department", type: "text" },
  { key: "location_summary", label: "Location Summary", type: "text", placeholder: "e.g. Distributed across all labs" },
  { key: "status", label: "Status", type: "select", options: ["in_use", "in_storage", "under_repair", "retired"] },
  { key: "qr_tagged", label: "QR-tagged", type: "checkbox" },
];

export const ASSET_KINDS = [
  { kind: "computers", label: "Desktop Computers" },
  { kind: "laptops", label: "Laptops" },
  { kind: "projectors", label: "Projectors" },
  { kind: "printers", label: "Printers" },
  { kind: "furniture", label: "Furniture" },
  { kind: "smart_boards", label: "Smart Boards" },
  { kind: "routers", label: "Routers" },
  { kind: "switches", label: "Network Switches" },
  { kind: "servers", label: "Servers" },
  { kind: "ups", label: "UPS Units" },
  { kind: "generators", label: "Generators" },
  { kind: "water_coolers", label: "Water Coolers" },
  { kind: "biometric_devices", label: "Biometric Devices" },
  { kind: "cctv_cameras", label: "CCTV Cameras" },
].map((f) => ({ ...f, fields: ASSET_COMMON }));
